/**
 * ChauffeurWisselDialog — chauffeur toewijzen of wisselen vanuit de kalender.
 *
 * Hergebruikt de AssignRouteDialog uit het rooster (beschikbaarheid, verlof,
 * dubbele diensten) en de schedule-store-acties (SF-route + dienst-rij in één
 * keer), zodat kalender en rooster dezelfde code delen. Laadt de roosterweek
 * van de route bij het openen; de kalender ververst na afloop.
 */
import { useEffect } from 'react';
import { AssignRouteDialog } from '../rooster/AssignRouteDialog';
import { useScheduleStore } from '../../stores/schedule-store';
import { useCalendarStore } from '../../stores/calendar-store';
import { staffRijMatchtSleutel } from '../../lib/staff-sleutel';
import type { RouteListItem } from '../../lib/salesforce';

interface ChauffeurWisselDialogProps {
  route: RouteListItem;
  onClose: () => void;
}

export function ChauffeurWisselDialog({ route, onClose }: ChauffeurWisselDialogProps) {
  const {
    staffList,
    schedule,
    availability,
    leaveEntries,
    weekStart,
    isLoading,
    isAssigning,
    assignError,
    setWeek,
    assignRoute,
    unassignRoute,
  } = useScheduleStore();
  const fetchRoutes = useCalendarStore((s) => s.fetchRoutes);

  // De roosterweek van de route laden (setWeek haalt de data op).
  useEffect(() => {
    setWeek(route.datum);
  }, [route.datum, setWeek]);

  const currentStaffId = route.chauffeurId
    ? staffList.find((s) => staffRijMatchtSleutel(s, route.chauffeurId!))?.id ?? null
    : null;

  const klaar = async (gelukt: boolean) => {
    if (!gelukt) return;
    await fetchRoutes();
    onClose();
  };

  // Tot de week geladen is toont de dialog een lege lijst; even wachten.
  const weekGeladen = weekStart && route.datum >= weekStart && !isLoading;
  if (!weekGeladen) {
    return (
      <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={onClose}>
        <div className="bg-white rounded-2xl px-6 py-5 shadow-xl text-sm text-gray-600">Rooster laden…</div>
      </div>
    );
  }

  return (
    <AssignRouteDialog
      route={route}
      staffList={staffList}
      schedule={schedule}
      availability={availability}
      leaveEntries={leaveEntries}
      currentStaffId={currentStaffId}
      onAssign={async (staffId) => klaar(await assignRoute(route.id, staffId, route.datum))}
      onUnassign={currentStaffId ? async () => klaar(await unassignRoute(route.id)) : undefined}
      onClose={onClose}
      isAssigning={isAssigning}
      error={assignError}
    />
  );
}
